import { LoginRequest, LoginResponse, User, UserPermissions, SessionStatus, AuthStatus } from '../types';

// When using Vite proxy, we need to use relative paths
// The proxy in package.json handles routing to http://localhost:8000
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

class AuthService {
  private baseURL: string;
  private csrfToken: string | null = null;

  constructor() {
    this.baseURL = `${API_BASE_URL}/users`;
    // Debug logging
    console.log('🔧 AuthService initialized with:', {
      API_BASE_URL,
      baseURL: this.baseURL,
      environment: import.meta.env.MODE,
      note: 'Using Create React App proxy from package.json'
    });
  }

  /**
   * Get CSRF token from backend
   */
  private async getCSRFToken(): Promise<string | null> {
    try {
      const response = await fetch(`${this.baseURL}/auth/csrf_token/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        this.csrfToken = data.csrf_token;
        return this.csrfToken;
      }
    } catch (error) {
      console.warn('⚠️ Failed to get CSRF token:', error);
    }
    return null;
  }

  /**
   * Get headers with CSRF token if available
   */
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Try to get CSRF token if we don't have one
    if (!this.csrfToken) {
      await this.getCSRFToken();
    }

    // Add CSRF token to headers if available
    if (this.csrfToken) {
      headers['X-CSRFToken'] = this.csrfToken;
    }

    return headers;
  }

  /**
   * Login user with username and password
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const loginURL = `${this.baseURL}/auth/login/`;
    
    // Debug logging
    console.log('🔐 Attempting login:', {
      url: loginURL,
      username: credentials.username,
      credentials: 'include'
    });

    try {
      // Get headers with CSRF token
      const headers = await this.getHeaders();
      
      const response = await fetch(loginURL, {
        method: 'POST',
        headers,
        credentials: 'include', // Include cookies for session management
        body: JSON.stringify(credentials),
      });

      console.log('📡 Login response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: response.headers ? Array.from(response.headers.entries()) : []
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Login failed' }));
        console.error('❌ Login failed:', errorData);
        
        // If CSRF error, clear token and try again
        if (errorData.detail && errorData.detail.includes('CSRF')) {
          this.csrfToken = null;
          console.log('🔄 CSRF error detected, clearing token and retrying...');
          // Don't retry automatically to avoid infinite loop
        }
        
        throw new Error(errorData.detail || 'Login failed');
      }

      const responseData = await response.json();
      console.log('✅ Login successful:', { user: responseData.user?.username, role: responseData.user?.role });
      return responseData;
    } catch (error) {
      console.error('🔥 Login error:', error);
      throw error;
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<{ message: string }> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseURL}/auth/logout/`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Logout failed' }));
        throw new Error(errorData.detail || 'Logout failed');
      }

      // Clear CSRF token after logout
      this.csrfToken = null;
      
      return response.json();
    } catch (error) {
      // Clear CSRF token even if logout fails
      this.csrfToken = null;
      throw error;
    }
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    const response = await fetch(`${this.baseURL}/auth/me/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to get user' }));
      throw new Error(errorData.detail || 'Failed to get current user');
    }

    return response.json();
  }

  /**
   * Get user permissions for UI control
   */
  async getUserPermissions(): Promise<UserPermissions> {
    const response = await fetch(`${this.baseURL}/permissions/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to get permissions' }));
      throw new Error(errorData.detail || 'Failed to get permissions');
    }

    return response.json();
  }

  /**
   * Check session status
   */
  async getSessionStatus(): Promise<SessionStatus> {
    const response = await fetch(`${this.baseURL}/session-status/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to get session status' }));
      throw new Error(errorData.detail || 'Failed to get session status');
    }

    return response.json();
  }

  /**
   * Check authentication status (doesn't require login)
   */
  async getAuthStatus(): Promise<AuthStatus> {
    try {
      // Ensure we have a CSRF token for authenticated requests
      if (!this.csrfToken) {
        await this.getCSRFToken();
      }

      const headers = await this.getHeaders();
      console.log('🔐 AuthService: Getting auth status with headers:', headers);
      
      const response = await fetch(`${this.baseURL}/auth-status/`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      console.log('🔐 AuthService: Auth status response:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.warn('⚠️ AuthService: Auth status failed:', response.status, errorText);
        return { authenticated: false, user: null };
      }

      const data = await response.json();
      console.log('✅ AuthService: Auth status success:', data);
      return data;
    } catch (error) {
      console.error('❌ AuthService: Auth status error:', error);
      return { authenticated: false, user: null };
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData: Partial<User>): Promise<{ message: string; user: User }> {
    const response = await fetch(`${this.baseURL}/auth/update_profile/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Profile update failed' }));
      throw new Error(errorData.detail || 'Profile update failed');
    }

    return response.json();
  }

  /**
   * Change user password
   */
  async changePassword(passwordData: { old_password: string; new_password: string }): Promise<{ message: string }> {
    const response = await fetch(`${this.baseURL}/auth/change_password/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(passwordData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Password change failed' }));
      throw new Error(errorData.detail || 'Password change failed');
    }

    return response.json();
  }

  /**
   * Check role permission for specific permit type
   */
  async checkRolePermission(permitType: string): Promise<any> {
    const response = await fetch(`${this.baseURL}/auth/check_role_permission/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ permit_type: permitType }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Permission check failed' }));
      throw new Error(errorData.detail || 'Permission check failed');
    }

    return response.json();
  }

  async searchRecipients(siteId: number, search: string): Promise<any[]> {
    const params = new URLSearchParams({ role: 'recipient', site: String(siteId), search });
    const response = await fetch(`/api/v1/users/?${params.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch recipients');
    }
    return response.json();
  }

  /**
   * Validate current session by making a test API call
   */
  async validateSession(): Promise<boolean> {
    try {
      console.log('🔍 AuthService: Validating session...');
      
      // Make a simple authenticated API call
      const headers = await this.getHeaders();
      const response = await fetch('/api/v1/sites/asset-owners/debug_auth/', {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      console.log('🔍 AuthService: Session validation response:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ AuthService: Session valid:', data);
        return data.is_authenticated === true;
      } else {
        const errorText = await response.text();
        console.warn('⚠️ AuthService: Session invalid:', response.status, errorText);
        return false;
      }
    } catch (error) {
      console.error('❌ AuthService: Session validation error:', error);
      return false;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService; 